import { Component } from "@theme/component";
import { setLocalStorage, getLocalStorage } from "@theme/utilities";

/**
 * Product recommendation quiz.
 *
 * Markup is rendered server-side by `snippets/quiz-funnel.liquid` (intro screen,
 * one fieldset per question, and one hidden panel per result). This element only
 * toggles visibility, scores the selected answers, and reveals the winning result.
 *
 * Scoring: each answer button carries `data-results="handle-a handle-b"`. Selecting
 * an answer adds +1 to every referenced result. Highest total wins; ties resolve to
 * the first result in DOM order (which mirrors the quiz's `results` order).
 *
 * Flow: selecting an answer auto-advances to the next question (or reveals the
 * result on the last question) after a short delay, so the visitor never has to
 * hunt for a "next" button. Set `data-auto-advance="false"` to require a tap on the
 * (currently unused) next control instead.
 *
 * @extends {Component<{
 *   intro: HTMLElement,
 *   questions: HTMLElement,
 *   results: HTMLElement,
 *   step: HTMLElement[],
 *   answer: HTMLButtonElement[],
 *   result: HTMLElement[],
 *   back: HTMLButtonElement,
 *   progressBar: HTMLElement,
 *   progressText: HTMLElement,
 * }>}
 */
class QuizFunnel extends Component {
  requiredRefs = ["intro", "questions", "results"];

  /** @type {number} */
  #total = 0;
  /** @type {number} */
  #step = 0;
  /** @type {number | undefined} */
  #advanceTimer;
  /**
   * Selected answer per step: `{ [stepIndex]: { index, results } }`.
   * @type {Record<number, { index: number, results: string[] }>}
   */
  #answers = {};

  connectedCallback() {
    super.connectedCallback();
    this.#total = Number(this.dataset.total) || this.refs.step.length;
    this.#restore();
  }

  get #storageKey() {
    return this.dataset.storageKey ?? "";
  }

  get #autoAdvance() {
    return this.dataset.autoAdvance !== "false";
  }

  /** Begin the quiz from the intro screen. */
  start() {
    this.refs.intro.hidden = true;
    this.refs.results.hidden = true;
    this.refs.questions.hidden = false;
    this.#goToStep(0);
  }

  /**
   * Record the chosen answer for its step (single-select), then auto-advance.
   * @param {Event} event
   */
  selectAnswer(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLButtonElement} */ (
      target.closest?.("[data-results]") ?? target
    );
    const step = Number(button.dataset.step);
    const results = (button.dataset.results ?? "").split(" ").filter(Boolean);

    for (const answer of this.refs.answer) {
      if (Number(answer.dataset.step) === step) {
        answer.setAttribute("aria-pressed", String(answer === button));
      }
    }

    this.#answers[step] = { index: this.refs.answer.indexOf(button), results };
    this.#persist();

    if (this.#autoAdvance) {
      clearTimeout(this.#advanceTimer);
      this.#advanceTimer = setTimeout(() => this.#advance(), 320);
    }
  }

  /** Advance to the next question, or reveal the result after the last one. */
  #advance() {
    if (this.#answers[this.#step] == null) return;
    if (this.#step >= this.#total - 1) {
      this.#showResult(this.#winningResult());
    } else {
      this.#goToStep(this.#step + 1);
    }
  }

  /** Public next control (used when auto-advance is disabled). */
  next() {
    this.#advance();
  }

  /** Go back a step, or return to the intro from the first step. */
  back() {
    clearTimeout(this.#advanceTimer);
    if (this.#step <= 0) {
      this.refs.questions.hidden = true;
      this.refs.intro.hidden = false;
      return;
    }
    this.#goToStep(this.#step - 1);
  }

  /** Clear all state and return to the intro screen. */
  restart() {
    clearTimeout(this.#advanceTimer);
    this.#answers = {};
    this.#step = 0;
    for (const answer of this.refs.answer) answer.setAttribute("aria-pressed", "false");
    for (const result of this.refs.result) result.hidden = true;
    if (this.#storageKey) window.localStorage?.removeItem(this.#storageKey);
    this.refs.results.hidden = true;
    this.refs.questions.hidden = true;
    this.refs.intro.hidden = false;
  }

  /**
   * Show the question fieldset for the given step and sync navigation state.
   * @param {number} index
   */
  #goToStep(index) {
    this.#step = index;
    for (const step of this.refs.step) {
      step.hidden = Number(step.dataset.step) !== index;
    }

    if (this.refs.back) this.refs.back.hidden = false;

    const progress = ((index + 1) / this.#total) * 100;
    this.refs.progressBar.style.inlineSize = `${progress}%`;
    this.refs.progressText.textContent = `${index + 1} / ${this.#total}`;
    this.#persist();
  }

  /**
   * Tally selected answers and return the handle of the highest-scoring result.
   * Ties resolve to the first result in DOM order.
   * @returns {string | null}
   */
  #winningResult() {
    /** @type {Record<string, number>} */
    const scores = {};
    for (const key in this.#answers) {
      for (const handle of this.#answers[key].results) {
        scores[handle] = (scores[handle] ?? 0) + 1;
      }
    }

    let winner = null;
    let best = -1;
    for (const result of this.refs.result) {
      const handle = result.dataset.result ?? "";
      const score = scores[handle] ?? 0;
      if (score > best) {
        best = score;
        winner = handle;
      }
    }
    return winner;
  }

  /**
   * Reveal the result panel for the given handle.
   * @param {string | null} handle
   */
  #showResult(handle) {
    if (handle == null) return;
    this.refs.questions.hidden = true;
    this.refs.intro.hidden = true;
    this.refs.results.hidden = false;

    for (const result of this.refs.result) {
      result.hidden = result.dataset.result !== handle;
    }

    this.#persist(handle);
  }

  /**
   * Save progress to localStorage (7-day expiry).
   * @param {string} [resultHandle]
   */
  #persist(resultHandle) {
    if (!this.#storageKey) return;
    setLocalStorage(
      this.#storageKey,
      { step: this.#step, answers: this.#answers, result: resultHandle ?? null },
      7
    );
  }

  /** Restore a saved session: re-apply selections and jump to the saved screen. */
  #restore() {
    if (!this.#storageKey) return;
    const saved = getLocalStorage(this.#storageKey);
    if (!saved || typeof saved !== "object") return;

    this.#answers = saved.answers ?? {};
    for (const key in this.#answers) {
      const answer = this.refs.answer[this.#answers[key].index];
      if (answer) answer.setAttribute("aria-pressed", "true");
    }

    if (saved.result) {
      this.#showResult(saved.result);
    } else if (Object.keys(this.#answers).length > 0) {
      this.refs.intro.hidden = true;
      this.refs.questions.hidden = false;
      this.#goToStep(saved.step ?? 0);
    }
  }
}

if (!customElements.get("quiz-funnel")) customElements.define("quiz-funnel", QuizFunnel);
